import React, { useState, useEffect } from "react";
import {
  Box,
  AppBar,
  Toolbar,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  useTheme,
  useMediaQuery,
  Divider,
  ListItemIcon,
  ListItemText,
  Skeleton,
} from "@mui/material";
// import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import ProfileIcon from "@diligentcorp/atlas-react-icons/dist/esm/lens/Profile.js";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import ApartmentIcon from "@mui/icons-material/Apartment";
import SignOutIcon from "@diligentcorp/atlas-react-icons/dist/esm/lens/SignOut.js";
import LoginIcon from "@mui/icons-material/Login";
import { userApiService, type UserProfile } from "../services/userApiService";
import { useOidcAuth } from "../contexts/OidcAuthContext";
import { AUTH_URLS } from "../utils/authUtils";

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const open = Boolean(anchorEl);

  // Use OIDC auth context
  const {
    isAuthenticated,
    user: oidcUser,
    login: oidcLogin,
    logout: oidcLogout,
  } = useOidcAuth();

  const theme = useTheme();
  // Use atlas theme breakpoints for responsive design
  const isXs = useMediaQuery(theme.breakpoints.down("sm")); // xs (0-sm)
  const isSm = useMediaQuery(theme.breakpoints.between("sm", "md")); // sm (sm-md)
  const isMd = useMediaQuery(theme.breakpoints.between("md", "lg")); // md (md-lg)

  // Calculate responsive horizontal padding based on atlas theme breakpoints
  const getHorizontalPadding = () => {
    if (isXs) return theme.spacing(2); // Spacing/2 = 16px
    if (isSm || isMd) return theme.spacing(3); // Spacing/3 = 24px
    return theme.spacing(4); // LG+: Spacing/4 = 32px
  };

  // Fetch user profile on component mount and when OIDC auth state changes
  // The /user/profile endpoint works for both authenticated and unauthenticated users
  // - Authenticated: Returns full user profile
  // - Unauthenticated: Returns Guest profile with organization info
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        // Always fetch profile (works for both public and private routes)
        const response = await userApiService.getUserProfile();

        if (response.success && response.data) {
          setUserProfile(response.data);
        } else {
          setUserProfile(null);
        }
      } catch (error) {
        console.error("Failed to fetch user profile:", error);
        setUserProfile(null);
      } finally {
        setLoadingProfile(false);
      }
    };

    fetchUserProfile();
  }, [isAuthenticated]);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleSignOut = () => {
    try {
      oidcLogout();
    } catch (error) {
      console.error("Failed to logout:", error);
    }
    handleMenuClose();
  };

  const handleMyProfile = () => {
    // Open OIDC profile URL in browser
    if (oidcUser?.profile) {
      // Use dynamic profile URL based on current environment
      const profileUrl = AUTH_URLS.profile();
      window.open(profileUrl, "_blank");
    }
    handleMenuClose();
  };

  const handleLogin = () => {
    try {
      oidcLogin();
    } catch (error) {
      console.error("Failed to redirect to login:", error);
    }
    handleMenuClose();
  };

  // const handleHelpClick = () => {
  //   console.log("Help clicked");
  // };

  return (
    <Box sx={{ width: "100vw", height: "100vh" }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          backgroundColor: "background.paper",
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Toolbar sx={{ justifyContent: "space-between", gap: 1 }}>
          {/* Left side - Organization name */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {loadingProfile ? (
              <>
                {/* Icon skeleton placeholder */}
                <Skeleton
                  variant="circular"
                  width={24}
                  height={24}
                  sx={{
                    bgcolor: "grey.200",
                    animation: "pulse 1.5s ease-in-out infinite",
                  }}
                />
                {/* Text skeleton placeholder with shadow effect */}
                <Skeleton
                  variant="text"
                  width={isXs ? 100 : isSm ? 120 : 140}
                  height={28}
                  sx={(theme) => ({
                    bgcolor: "grey.200",
                    borderRadius: "4px",
                    animation: "pulse 1.5s ease-in-out infinite",
                    boxShadow: 1,
                    "&::after": {
                      background: `linear-gradient(90deg, transparent, ${theme.palette.common.white}66, transparent)`,
                    },
                  })}
                />
              </>
            ) : (
              <>
                <ApartmentIcon sx={{ color: "text.primary" }} />
                <Typography
                  variant="h6"
                  sx={{
                    color: "text.primary",
                    fontWeight: 600,
                    fontSize: theme.typography.subtitle1.fontSize,
                  }}
                >
                  {userProfile?.organization?.name || "Policy Portal"}
                </Typography>
              </>
            )}
          </Box>

          {/* Right side - Help and User menu */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {/* <IconButton onClick={handleHelpClick} aria-label="help">
              <HelpOutlineIcon />
            </IconButton> */}
            <IconButton
              onClick={handleMenuClick}
              aria-controls={open ? "user-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={open ? "true" : undefined}
              aria-label="user menu"
            >
              <AccountCircleOutlinedIcon />
            </IconButton>
            <Menu
              id="user-menu"
              anchorEl={anchorEl}
              open={open}
              onClose={handleMenuClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              PaperProps={{
                sx: { minWidth: 200 },
              }}
            >
              {isAuthenticated && userProfile
                ? [
                    /* User name header */
                    <MenuItem
                      key="user-name"
                      disabled
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          width: "100%",
                          color: "text.primary",
                        }}
                      >
                        {userProfile.user.name}
                      </Typography>
                    </MenuItem>,

                    <Divider
                      key="divider"
                      component="li"
                      sx={{ my: 0, "&&": { mt: 0 } }}
                    />,

                    /* My Profile */
                    <MenuItem key="my-profile" onClick={handleMyProfile}>
                      <ListItemIcon>
                        <ProfileIcon />
                      </ListItemIcon>
                      <ListItemText primary="My Profile" />
                    </MenuItem>,

                    /* Log Out */
                    <MenuItem key="log-out" onClick={handleSignOut}>
                      <ListItemIcon>
                        <SignOutIcon />
                      </ListItemIcon>
                      <ListItemText primary="Log Out" />
                    </MenuItem>,
                  ]
                : [
                    /* Not authenticated - show Log In option */
                    <MenuItem key="log-in" onClick={handleLogin}>
                      <ListItemIcon>
                        <LoginIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary="Log In" />
                    </MenuItem>,
                  ]}
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>
      <Box
        component="main"
        data-atlas-gn-app
        sx={(theme) => ({
          width: "100vw",
          height: "100vh",
          overflow: "auto",
          paddingTop: `calc(64px + ${theme.spacing(3)})`, // 64px for AppBar height + Spacing/3 top padding
          paddingLeft: getHorizontalPadding(),
          paddingRight: getHorizontalPadding(),
          paddingBottom: theme.spacing(2), // Spacing/2
          boxSizing: "border-box",
        })}
      >
        {children}
      </Box>
    </Box>
  );
};

export default Layout;
